import type { BacktestRunDto, UserRole } from '@/api/types';

export type BacktestStatusTone = 'success' | 'warning' | 'danger' | 'info';

export function isActiveRun(run: BacktestRunDto) {
  return run.status === 'Queued' || run.status === 'Running';
}

export function getNetPnl(run: BacktestRunDto) {
  if (run.netPnl != null) return run.netPnl;
  if (isCertifiedRun(run)) return null;
  if (run.finalEquity == null || run.accountSize == null || run.accountSize <= 0) return null;
  return run.finalEquity - run.accountSize;
}

export function getReturnPercent(run: BacktestRunDto) {
  if (run.returnPercent != null) return run.returnPercent;
  if (isCertifiedRun(run)) return null;
  const netPnl = getNetPnl(run);
  return netPnl == null || run.accountSize == null ? null : (netPnl / run.accountSize) * 100;
}

export function getWinRatePercent(run: BacktestRunDto) {
  if (run.winRatePercent != null) return run.winRatePercent;
  if (isCertifiedRun(run)) return null;
  const decidedTrades = (run.winningTrades ?? 0) + (run.losingTrades ?? 0);
  return decidedTrades > 0 ? ((run.winningTrades ?? 0) / decidedTrades) * 100 : null;
}

export function isCertifiedRun(run: BacktestRunDto) {
  return run.isCertified ?? ((run.metricsVersion ?? 1) >= 3 && run.executionErrorCount === 0);
}

export function canCreateDeploymentFromBacktest(run: BacktestRunDto, role: UserRole | undefined) {
  return (role === 'Trader' || role === 'Admin') && run.status === 'Succeeded' && isCertifiedRun(run);
}

export function getBacktestStatusTone(status: string | null | undefined): BacktestStatusTone {
  if (status === 'Succeeded') return 'success';
  if (status === 'Failed') return 'danger';
  if (status === 'Queued' || status === 'Running') return 'warning';
  return 'info';
}

export function formatSigned(value: number, fractionDigits = 2) {
  return `${value >= 0 ? '+' : ''}${value.toFixed(fractionDigits)}`;
}

export function formatDateTime(value: string | null | undefined) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

export function formatShortDate(value: string | null | undefined) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}
